import React, { useState } from "react";
import { graphql, useFragment } from "react-relay";

import { AppContext } from "~/common";
import { ReactComponent as Sparkles } from "~/meetingRecorder/css/ask-copilot-suggested-question-sparkle.svg";
import { useAnalyticsTrack } from "~/meetingRecorder/utils";

import type {
	AskFellowSuggestedQuestions_EmptyState_conversation$data,
	AskFellowSuggestedQuestions_EmptyState_conversation$key,
} from "~/__generated__/AskFellowSuggestedQuestions_EmptyState_conversation.graphql";
import type { AskFellowSuggestedQuestions_SuggestedQuestionButton_suggestedQuestion$key } from "~/__generated__/AskFellowSuggestedQuestions_SuggestedQuestionButton_suggestedQuestion.graphql";

import styles from "./AskFellowPanel.module.css";

const SUGGESTED_QUESTIONS_VISIBLE_COUNT = 4;

interface Props {
	conversation: AskFellowSuggestedQuestions_EmptyState_conversation$key;
	sendMessage: (content: string, messageId: string, contentForDisplay?: string) => void;
}

function SuggestedQuestionButton({
	conversation,
	suggestedQuestion: suggestedQuestionKey,
	sendMessage,
}: {
	conversation: AskFellowSuggestedQuestions_EmptyState_conversation$data;
	suggestedQuestion: AskFellowSuggestedQuestions_SuggestedQuestionButton_suggestedQuestion$key;
	sendMessage: (content: string, messageId: string, contentForDisplay?: string) => void;
}) {
	const suggestedQuestion = useFragment(
		graphql`
			fragment AskFellowSuggestedQuestions_SuggestedQuestionButton_suggestedQuestion on SuggestedQuestion {
				id
				content
				contentForDisplay
			}
		`,
		suggestedQuestionKey,
	);

	const dispatchAnalyticsTrack = useAnalyticsTrack();

	const onClick = React.useCallback(() => {
		sendMessage(suggestedQuestion.content, suggestedQuestion.id, suggestedQuestion.contentForDisplay ?? undefined);
		dispatchAnalyticsTrack("[Ask Copilot] Sent suggested question", {
			// eslint-disable-next-line camelcase
			suggested_question_id: suggestedQuestion.id,
			context: conversation?.context?.name,
		});
	}, [sendMessage, suggestedQuestion, dispatchAnalyticsTrack, conversation?.context?.name]);

	const { account } = React.useContext(AppContext);

	return (
		<button
			key={suggestedQuestion.id}
			onClick={onClick}
			className={styles.suggestedQuestionButton}
			disabled={!account?.allowAiFeatures}
		>
			<div className={styles.suggestedQuestionButtonIcon}>
				<Sparkles />
			</div>
			<div className={styles.suggestedQuestionButtonText}>
				{suggestedQuestion.contentForDisplay ?? suggestedQuestion.content}
			</div>
		</button>
	);
}

const AskFellowSuggestedQuestions: React.FC<Props> = ({ conversation: conversationKey, sendMessage }) => {
	const conversation = useFragment(
		graphql`
			fragment AskFellowSuggestedQuestions_EmptyState_conversation on AskCopilotConversation {
				suggestedQuestions {
					id
					...AskFellowSuggestedQuestions_SuggestedQuestionButton_suggestedQuestion
				}
				context {
					name
					filters
				}
			}
		`,
		conversationKey,
	);

	const [startIndex, setStartIndex] = useState(0);
	const totalQuestions = conversation.suggestedQuestions.length;

	// Compute the visible questions in a circular fashion
	const visibleQuestions = Array.from(
		{ length: Math.min(SUGGESTED_QUESTIONS_VISIBLE_COUNT, totalQuestions) },
		(_, i) => {
			return conversation.suggestedQuestions[(startIndex + i) % totalQuestions];
		},
	);

	const handleShowMore = () => {
		setStartIndex(prev => (prev + SUGGESTED_QUESTIONS_VISIBLE_COUNT) % totalQuestions);
	};

	const canShowMore = totalQuestions > SUGGESTED_QUESTIONS_VISIBLE_COUNT;

	return (
		<div className={styles.suggestedQuestionsWrapper}>
			{visibleQuestions.map(question => (
				<SuggestedQuestionButton
					conversation={conversation}
					key={question.id}
					suggestedQuestion={question}
					sendMessage={sendMessage}
				/>
			))}
			{canShowMore && (
				<button className={styles.suggestedQuestionButton} onClick={handleShowMore} type="button">
					<div className={styles.suggestedQuestionButtonText}>Show more questions</div>
				</button>
			)}
		</div>
	);
};

export default AskFellowSuggestedQuestions;
